import React, { useMemo } from 'react';
import { useCalculatorStore } from '../../store/calculatorStore';
import { formatCurrency } from '../../utils/format';
import {
  getDisplayedMonthlyPayment,
  getPaymentFrequencyPaymentLabel,
} from '../../utils/monthlyPaymentDisplay';
import { scrollElementTopBelowAppChrome } from '../../utils/scrollBelowAppChrome';
import { trackEvent } from '../../utils/analytics';

interface LivePaymentBottomBarProps {
  hasScenario2: boolean;
  onAddScenario: () => void;
}

export const LivePaymentBottomBar: React.FC<LivePaymentBottomBarProps> = ({
  hasScenario2,
  onAddScenario,
}) => {
  const activeTabId = useCalculatorStore(s => s.activeTabId);
  const calculatorTabs = useCalculatorStore(s => s.calculatorTabs);

  const activeTab = useMemo(
    () => calculatorTabs.find(t => t.id === activeTabId),
    [calculatorTabs, activeTabId],
  );

  const monthlyPayment = useMemo(() => getDisplayedMonthlyPayment(activeTab), [activeTab]);

  const country = activeTab?.data.mortgageTerms?.country || 'USA';
  const paymentFrequency = activeTab?.data.mortgageTerms?.paymentFrequency;

  const frequencyPaymentLabel = useMemo(
    () => getPaymentFrequencyPaymentLabel(paymentFrequency),
    [paymentFrequency],
  );

  const formattedPayment = useMemo(() => {
    if (monthlyPayment === null) return '$--';
    return formatCurrency(monthlyPayment, country).replace(/\.00$/, '');
  }, [monthlyPayment, country]);

  if (activeTabId === 'compare') return null;

  const scrollTo = (selector: string, eventName: string) => {
    trackEvent(eventName, {
      tab_id: activeTabId,
      has_scenario_2: hasScenario2,
    });
    scrollElementTopBelowAppChrome(selector, { behavior: 'smooth' });
  };

  return (
    <div
      className="lg:hidden fixed inset-x-0 bottom-0 z-[45] border-t border-navy-700 bg-navy-900 shadow-[0_-4px_24px_rgba(0,0,0,0.25)] pb-[env(safe-area-inset-bottom)]"
      role="region"
      aria-label="Live monthly payment"
    >
      <div className="flex min-h-12 items-center gap-2 px-3">
        <div className="flex min-w-0 flex-1 gap-2">
          <div className="flex h-[1.125rem] shrink-0 items-center">
            <span className="flex items-center gap-1 rounded-full bg-live-light px-1.5 py-0.5 text-[10px] font-medium text-live">
              <span className="h-1.5 w-1.5 rounded-full bg-live animate-pulse" aria-hidden="true" />
              LIVE
            </span>
          </div>
          <div className="min-w-0">
            <p
              className="truncate font-serif text-lg leading-none text-gold-500"
              aria-live="polite"
              aria-atomic="true"
            >
              {formattedPayment}
              <span className="sr-only"> {frequencyPaymentLabel.toLowerCase()}</span>
            </p>
            <p className="truncate text-[9px] font-semibold uppercase tracking-widest text-navy-400 mt-0.5">
              {frequencyPaymentLabel}
            </p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1 border-l border-navy-700 pl-2">
          <button
            type="button"
            onClick={() => scrollTo('#live-payment-breakdown', 'live_payment_bar_breakdown_click')}
            className="min-h-11 px-2 text-xs font-semibold leading-tight text-center text-navy-100 hover:text-gold-300 transition-colors"
          >
            Payment
            <br />
            Breakdown
          </button>
          {hasScenario2 ? (
            <button
              type="button"
              onClick={() => scrollTo('#live-payment-comparison', 'live_payment_bar_comparison_click')}
              className="min-h-11 px-2 text-xs font-semibold leading-tight text-center text-gold-400 hover:text-gold-300 transition-colors"
            >
              Comparison
              <br />
              Preview
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                trackEvent('live_payment_bar_add_scenario_click', { tab_id: activeTabId });
                onAddScenario();
              }}
              className="min-h-11 px-2 text-xs font-semibold leading-tight text-center text-gold-400 hover:text-gold-300 transition-colors"
            >
              Add new
              <br />
              scenario
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
